import React, { useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classNames from 'classnames';
import { START_DATE, END_DATE, timeOfDayFromTimeZoneToLocal, getStartOf, timeOfDayFromLocalToTimeZone } from '../../../../util/dates';
import { FormattedMessage, useIntl } from '../../../../util/reactIntl';
import { useConfiguration } from '../../../../context/configurationContext';
import { DateRangeInput } from '../../../../components/FieldDateRangeInput/FieldDateRangeInput';
import { useHasUserInteracted } from '../../../../hooks/useHasUserInteracted';
import css from './DateRangePicker.module.css';

const DateRangePickerComponent = React.forwardRef(({
  startDate,
  endDate,
  onDatesChange,
  minimumNights = 0,
  timeZone,
  onFieldFocusScroll,
  onPickerClose,
}, ref) => {

  const config = useConfiguration();
  const intl = useIntl();
  const [focusedInput, setFocusedInput] = useState(null);
  const [pendingStartDate, setPendingStartDate] = useState(null);
  const [pendingEndDate, setPendingEndDate] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const hasUserInteractedRef = useHasUserInteracted();

  useImperativeHandle(ref, () => ({
    focus: () => {
      setFocusedInput(START_DATE);
    },
    close: () => {
      setFocusedInput(null);
    },
  }));

  // Validate date objects - ensure they are valid Date instances
  const isValidDate = (date) => {
    return date instanceof Date && !isNaN(date.getTime()) && moment(date).isValid();
  };

  const validatedStartDate = isValidDate(startDate) ? startDate : null;
  const validatedEndDate = isValidDate(endDate) ? endDate : null;

  // ZONE CONTRACT. The applied range (startDate/endDate props, StoreProfilePage's
  // filters.dateRange) is a pair of INSTANTS: midnight of the chosen days in the
  // STORE's zone - the zone cart items are stamped with, the URL is written in, and
  // the panel/pricing format in. DateRangeInput, however, works purely in the
  // browser's zone (react-dates hands back local midnights, and it ignores the
  // `format`/`parse` props - those only exist on the Final Form wrapper), so the two
  // spaces are bridged explicitly here: applied instants -> the same wall-clock day
  // in browser-local for display, and picked local days -> store-zone midnight on
  // Apply. Without this bridge the picker emitted browser-local midnights that were
  // then labelled with the store's zone - for any browser not in the store's zone
  // that is a different day, which is how "I picked Oct 28-30" showed up as Oct 27-29
  // in the cart panel. No zone (store without coordinates) falls back to browser-local
  // on both sides, consistently.
  const toPickerDate = instant =>
    instant && timeZone ? timeOfDayFromTimeZoneToLocal(instant, timeZone) : instant;
  const toAppliedInstant = pickerMoment =>
    timeZone
      ? getStartOf(timeOfDayFromLocalToTimeZone(pickerMoment.toDate(), timeZone), 'day', timeZone)
      : pickerMoment.clone().startOf('day').toDate();

  // Convert separate props to value object expected by DateRangeInput
  // When calendar is open, show pending dates, otherwise show applied dates
  const showPending = focusedInput !== null;
  const value = {
    startDate: showPending
      ? (pendingStartDate ? pendingStartDate.startOf('day').toDate() : null)
      : toPickerDate(validatedStartDate),
    endDate: showPending
      ? (pendingEndDate ? pendingEndDate.startOf('day').toDate() : null)
      : toPickerDate(validatedEndDate),
  };

  const handleDatesChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    // Validate dates before setting pending state
    const validStartDate = newStartDate && moment(newStartDate).isValid() ? moment(newStartDate) : null;
    const validEndDate = newEndDate && moment(newEndDate).isValid() ? moment(newEndDate) : null;

    setPendingStartDate(validStartDate);
    setPendingEndDate(validEndDate);

    // Auto-focus endDate after startDate is selected
    if (validStartDate && !validEndDate && focusedInput === START_DATE) {
      // Small delay to ensure state updates
      setTimeout(() => {
        setFocusedInput(END_DATE);
      }, 0);
    }
  };


  const onApply = () => {
    if (isApplying) {
      // This is automatic application from DateRangeInput onFocusChange, ignore it
      return;
    }

    // Validate dates before applying - both must be present and valid
    if (pendingStartDate && pendingEndDate) {
      // Ensure both dates are valid moments
      if (!moment(pendingStartDate).isValid() || !moment(pendingEndDate).isValid()) {
        // Invalid dates, don't apply
        return;
      }

      // pendingEndDate is already the EXCLUSIVE checkout day (DateRangeInput's
      // pickerEndDateToApiDate adds the day) - only the zone is bridged here.
      const start = toAppliedInstant(pendingStartDate);
      const end = toAppliedInstant(pendingEndDate);

      // Validate the Date objects are valid
      if (!isValidDate(start) || !isValidDate(end)) {
        // Invalid dates, don't apply
        return;
      }

      // Ensure endDate is not before startDate
      if (moment(end).isBefore(moment(start))) {
        // Invalid range, don't apply
        return;
      }

      onDatesChange({ startDate: start, endDate: end });
    } else {
      // Closed without a complete new range (Apply with nothing picked, or tapping
      // outside after picking only a start). The applied range is untouched - drop the
      // partial pending selection so the inputs show the applied dates again.
      setPendingStartDate(null);
      setPendingEndDate(null);
    }

    // Close calendar
    setFocusedInput(null);
    onPickerClose?.();
  };

  // Clear button inside the calendar. DateRangeInput has already dropped its own
  // selection and reset its focus to Start Date by the time this runs, but the range that
  // actually drives the page - card availability, the dates a cart item is created with,
  // the startDate/endDate URL params - lives in StoreProfilePage's filters.dateRange and
  // is only reachable through onDatesChange. Without pushing the clear up there, the
  // inputs read empty while the grid stays filtered by the range the user just cleared,
  // and the old dates reappear in the inputs as soon as the calendar closes.
  const handleCancel = () => {
    setPendingStartDate(null);
    setPendingEndDate(null);
    if (validatedStartDate || validatedEndDate) {
      onDatesChange({ startDate: null, endDate: null });
    }
  };

  // Convert onDatesChange to onChange expected by DateRangeInput
  const handleChange = ({ startDate: newStartDate, endDate: newEndDate }) => {
    // DateRangeInput's onChange provides Date objects, validate and convert to moments for handleDatesChange
    const startMoment = newStartDate && isValidDate(newStartDate) ? moment(newStartDate) : null;
    const endMoment = newEndDate && isValidDate(newEndDate) ? moment(newEndDate) : null;
    handleDatesChange({ startDate: startMoment, endDate: endMoment });
  };

  // Handle focus/blur events
  const handleFocus = (nextFocusedInput) => {
    // Scroll the field into view on a genuine new focus (not on close, not on the
    // auto-advance-to-End-Date flow in handleDatesChange, which calls setFocusedInput
    // directly and never goes through here) so the calendar opens fully visible on
    // mobile instead of low on the page, hidden behind browser/keyboard chrome.
    const isNewFocus = nextFocusedInput && nextFocusedInput !== focusedInput;
    setFocusedInput(nextFocusedInput);

    if (isNewFocus && onFieldFocusScroll) {
      onFieldFocusScroll();
    }

    // Focusing Start Date means "re-pick the whole range": empty both inputs and leave
    // the calendar open on Start Date. Only the PENDING selection is reset here - the
    // applied range (StoreProfilePage's filters.dateRange, which drives card
    // availability, the dates a cart item is created with and the URL) is deliberately
    // left alone until the user Applies a complete new range or explicitly hits Clear.
    // It used to be wiped on this focus, so a shopper who opened the calendar to correct
    // their dates and then tapped away without finishing was left with no dates at all:
    // every card's availability re-fetched, quantities reset, and (before the store
    // cart stopped being scoped to the filter) their whole cart appeared to vanish.
    // See useHasUserInteracted for why a focus event only counts once the user has
    // actually touched the page - react-dates emits focus of its own while props settle.
    const hasPendingToClear = pendingStartDate || pendingEndDate;
    if (
      nextFocusedInput === START_DATE &&
      hasUserInteractedRef.current &&
      (hasPendingToClear || validatedStartDate || validatedEndDate)
    ) {
      setPendingStartDate(null);
      setPendingEndDate(null);
    }
  };

  // Fired (debounced, from DateRangeInput) once the calendar has actually been dismissed
  // without applying - tapping outside it. Bring the picker's own focus state back in
  // sync so the inputs stop showing the abandoned pending selection and show the
  // applied range again; a dismissal is a cancel, not a clear.
  const handleBlur = () => {
    if (focusedInput === null) return;
    setPendingStartDate(null);
    setPendingEndDate(null);
    setFocusedInput(null);
    onPickerClose?.();
  };

  // Determine input highlight state
  const isStartFocused = focusedInput === START_DATE;
  const isEndFocused = focusedInput === END_DATE;
  // Show red highlight if start date is empty or invalid (even when not focused)
  const showStartHighlight = isStartFocused || !value.startDate || !isValidDate(value.startDate);
  return (
    <div className={css.root}>
      {/* <h3 className={css.title}>
        <FormattedMessage
          id="StoreProfilePage.Sidebar.selectRentalDates"
          defaultMessage="Select Rental Dates"
        />
      </h3> */}
      <div
        className={classNames(css.inputsWrapper, {
          [css.startHighlight]: showStartHighlight,
          [css.endHighlight]: isEndFocused,
        })}
      >
        <DateRangeInput
          value={value}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onApply={onApply}
          onRemove={handleCancel}
          isDaily={true}
          isOutsideRange={(focusedInput) => (day) => {
            // Disable past dates - day must be today or later
            return moment(day).isBefore(moment(), 'day');
          }}
          isDayBlocked={(focusedInput) => (day) => false}
          minimumNights={minimumNights}
          focusedInput={focusedInput}
          startDateId="startDate"
          endDateId="endDate"
          // Without this the footer button falls back to "Cancel" - but its action is
          // to clear the applied dates (handleCancel above), not to back out. A shopper
          // who tapped "Cancel" to get out of the calendar lost their date filter, and
          // with it everything the filter drives. Label it for what it does.
          clearButtonLabel={intl.formatMessage({
            id: 'StoreProfilePage.dateRange.clearDates',
            defaultMessage: 'Clear',
          })}
          startDatePlaceholderText={intl.formatMessage({
            id: 'StoreProfilePage.dateRange.startDatePlaceholder',
            defaultMessage: 'Select start date',
          })}
          endDatePlaceholderText={intl.formatMessage({
            id: 'StoreProfilePage.dateRange.endDatePlaceholder',
            defaultMessage: 'Select end date',
          })}
          numberOfMonths={2}
          keepOpenOnDateSelect={true}
          withPortal={false}
          firstDayOfWeek={config.localization.firstDayOfWeek}
          daySize={41}
          hideKeyboardShortcutsPanel={true}
          customInputIcon={null}
          showDefaultInputIcon={false}
          noBorder={false}
          block={false}
          useMobileMargins={true}
        />
      </div>
    </div>
  );
});

DateRangePickerComponent.displayName = 'DateRangePickerComponent';
DateRangePickerComponent.propTypes = {
  startDate: PropTypes.instanceOf(Date),
  endDate: PropTypes.instanceOf(Date),
  onDatesChange: PropTypes.func.isRequired,
  minimumNights: PropTypes.number,
  timeZone: PropTypes.string,
  onFieldFocusScroll: PropTypes.func,
  onPickerClose: PropTypes.func,
};

export default DateRangePickerComponent;
